import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { isUndefined } from 'lodash';
import { Subject } from 'rxjs';
import { LocalizationService } from '../../../localization/localization.service';
import { IFormatAsNumberOptions } from '../../../shared/directives/format-as-number/format-as-number.directive';
import { MoneyPipe } from '../../../shared/pipes/money/money.pipe';
import {
  ICurrencyInfo,
  MoneyService,
} from '../../../shared/services/money/money.service';
import {
  ITrackingAction,
  ITrackingObject,
} from '../../../tracking/tracking.data';
import { FormAlignmentsEnum } from '../../form-master.enum';
import { IFormFieldErrorMessages } from '../../form-master.interface';
import { CustomFormFieldComponent } from '../custom-form-field/custom-form-field.component';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-currency-field',
  templateUrl: './currency-field.component.html',
  styleUrls: ['./currency-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyFieldComponent),
      multi: true,
    },
  ],
})
export class CurrencyFieldComponent
  extends CustomFormFieldComponent
  implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() alignment: FormAlignmentsEnum;
  @Input() label: string;
  @Input() hideLabel: boolean;
  @Input() fieldId: string;
  @Input() condensed: boolean;
  @Input() currencyCode: string;
  @Input() locale: string;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingAction: ITrackingAction;
  @Input() formControl: FormControl;
  @Input() allowNegative: boolean;
  @Input() e2eId: string;
  @Input() errorMessages: IFormFieldErrorMessages;

  @Output() onFocus: EventEmitter<void> = new EventEmitter<void>();
  @Output() onBlur: EventEmitter<void> = new EventEmitter<void>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _currencyInfo: ICurrencyInfo;

  internalFormControl: AbstractControl;
  initialValue: string;
  formatAsNumberOptions: IFormatAsNumberOptions;

  constructor(
    changeDetectorRef: ChangeDetectorRef,
    translateService: TranslateService,
    private _moneyService: MoneyService,
    private _localizationService: LocalizationService,
    private _moneyPipe: MoneyPipe
  ) {
    super(changeDetectorRef, translateService);
  }

  ngOnInit() {
    this.currencyCode = this.currencyCode || 'USD';
    this.locale = this.locale || 'en-US';
    this.fieldControl = this.formControl;

    this._currencyInfo = this._moneyService.getCurrencyInfo(this.currencyCode);

    this.formatAsNumberOptions = {
      decimalDigits: this._currencyInfo.decimalDigits,
      onBlur: this._convertToCurrency.bind(this),
      allowNegative: this.allowNegative,
      decimalSeparator: this._localizationService.getDecimalSeparator(
        this.locale
      ),
    };

    super.ngOnInit();
  }

  writeValue(value: any) {
    if (!isUndefined(value) && !this.internalFormControl) {
      this.initFormControl(
        this._moneyService.subToSuperUnit(value, this.currencyCode)
      );
    } else {
      this.internalFormControl.setValue(
        this._moneyPipe.transform(value, this.currencyCode, this.locale),
        { emitEvent: false }
      );
    }
  }

  setDisabledState(isDisabled: boolean) {
    const onlySelf = { onlySelf: true, emitEvent: false };

    if (isDisabled) {
      this.internalFormControl.disable(onlySelf);
    } else {
      this.internalFormControl.enable(onlySelf);
    }
  }

  initFormControl(value: any) {
    this.internalFormControl = new FormControl(value);
    this.initialValue = this._moneyPipe.transform(
      this._moneyService.superToSubUnit(value, this.currencyCode),
      this.currencyCode,
      this.locale
    ) as string;

    this.internalFormControl.valueChanges
      .pipe(takeUntil(this._destroyed))
      .subscribe((currentAmount) => {
        const formattedValue = currentAmount
          ? currentAmount.replace(
              this.formatAsNumberOptions.decimalSeparator,
              '.'
            )
          : '0';

        this.propagateChange(
          this._moneyService.superToSubUnit(formattedValue, this.currencyCode)
        );
      });
  }

  private _convertToCurrency(value: string): string {
    const formattedValue = value
      ? value.replace(this.formatAsNumberOptions.decimalSeparator, '.')
      : '0';

    return this._moneyPipe.transform(
      this._moneyService.superToSubUnit(formattedValue, this.currencyCode),
      this.currencyCode,
      this.locale
    ) as string;
  }
}
